<script setup lang="ts">
import { collectAnswerableAssessmentBlocks } from "#bb/lib/assessmentAnswers";
import {
  findComponentStateConditionCycles,
  GENERIC_COMPONENT_STATE_FIELDS,
  validateConditionalRules,
  type ConditionStateField,
  type ConditionalRule,
  type ConditionalRuleValidationError,
} from "#bb/lib/conditionalRules";
import RuleCard from "../rules/RuleCard.vue";
import {
  createDraftRule,
  flattenBlocks,
  isDraftRuleComplete,
  loadDraftRules,
  resolveFieldMeta,
  serializeDraftRules,
  type AnswerableBlockSummary,
  type DraftRule,
} from "../rules/ruleModel";

const props = defineProps<{
  blockId: string;
  variantId: string;
  conditions: ConditionalRule[];
  open?: boolean;
  showTrigger?: boolean;
}>();
const emit = defineEmits<{
  save: [conditions: ConditionalRule[]];
  "update:open": [value: boolean];
}>();

const { contentState } = useContentManager();
const { getBlock } = useBlockRegistry();
const internalOpen = shallowRef(false);
const draftRules = ref<DraftRule[]>([]);
const dialogOpen = computed({
  get: () => props.open ?? internalOpen.value,
  set: (value: boolean) => {
    internalOpen.value = value;
    emit("update:open", value);
  },
});

const allBlocks = computed(() =>
  flattenBlocks(contentState.value).map((block) => ({
    ...block,
    type: getBlock(block.componentId ?? "")?.type,
    icon: getBlock(block.componentId ?? "")?.icon,
  })),
);
const answerableBlocks = computed<AnswerableBlockSummary[]>(() =>
  collectAnswerableAssessmentBlocks(contentState.value).map((block) => ({
    id: block.id,
    componentId: block.componentId,
    label: block.label,
    answerType: block.answerType,
    options: block.options,
    type: getBlock(block.componentId)?.type,
    icon: getBlock(block.componentId)?.icon,
  })),
);
const allBlocksById = computed(() =>
  new Map(allBlocks.value.map((block) => [block.id, block])),
);
const blockIds = computed(() =>
  new Set(allBlocks.value.map((block) => block.id)),
);

function getComponentStateFields(componentId?: string): ConditionStateField[] {
  return [
    ...GENERIC_COMPONENT_STATE_FIELDS,
    ...(getBlock(componentId ?? "")?.conditionStateFields ?? []),
  ];
}

const ruleMetas = computed(() =>
  draftRules.value.map((rule) =>
    resolveFieldMeta(rule.source, {
      answerableBlocks: answerableBlocks.value,
      allBlocksById: allBlocksById.value,
      getComponentStateFields,
    }),
  ),
);
const serializedRules = computed(() =>
  serializeDraftRules(draftRules.value).map((rule) => ({
    ...rule,
    action: undefined,
  })),
);
const validationErrors = computed(() =>
  validateConditionalRules(serializedRules.value, {
    currentBlockId: props.blockId,
    blockIds: blockIds.value,
    getComponentStateFields: (blockId) => {
      const block = allBlocksById.value.get(blockId);
      return getComponentStateFields(block?.componentId).map((field) => field.key);
    },
    requireAction: false,
  }),
);

function contentWithDraftRules(nodes: unknown): unknown {
  if (!Array.isArray(nodes)) return nodes;
  return nodes.map((node) => {
    if (!node || typeof node !== "object") return node;
    const block = node as Record<string, any>;
    const items = Array.isArray(block.items)
      ? block.items.map((item: Record<string, any>) => ({
          ...item,
          variants: Array.isArray(item.variants)
            ? item.variants.map((variant: Record<string, any>) => ({
                ...variant,
                conditions:
                  block.id === props.blockId && variant.id === props.variantId
                    ? serializedRules.value
                    : variant.conditions,
              }))
            : item.variants,
        }))
      : block.items;
    return {
      ...block,
      items,
      children: contentWithDraftRules(block.children),
    };
  });
}

const hasCurrentBlockCycle = computed(() =>
  findComponentStateConditionCycles(
    contentWithDraftRules(contentState.value),
  ).some((cycle) => cycle.includes(props.blockId)),
);
const canSave = computed(() =>
  draftRules.value.length > 0
  && draftRules.value.every((rule, index) =>
    isDraftRuleComplete(
      rule,
      ruleMetas.value[index],
      { requireAction: false },
    ),
  )
  && validationErrors.value.length === 0
  && !hasCurrentBlockCycle.value,
);

function errorsForRule(index: number): ConditionalRuleValidationError[] {
  return validationErrors.value.filter((error) => error.index === index);
}

function openDialog() {
  draftRules.value = loadDraftRules(props.conditions).map((rule) => ({
    ...rule,
    action: "",
  }));
  dialogOpen.value = true;
}

function save() {
  if (!canSave.value) return;
  emit("save", serializedRules.value);
  dialogOpen.value = false;
}

watch(
  () => dialogOpen.value,
  (open, wasOpen) => {
    if (open && !wasOpen) {
      draftRules.value = loadDraftRules(props.conditions).map((rule) => ({
        ...rule,
        action: "",
      }));
    }
  },
);
</script>

<template>
  <Button
    v-if="showTrigger ?? true"
    type="button"
    variant="outline"
    size="sm"
    class="w-full justify-start rounded-sm text-[12.5px]"
    @click="openDialog"
  >
    <Icon name="lucide:git-branch" class="mr-2 size-4" />
    Conditions
    <Badge variant="secondary" class="ml-auto">{{ conditions.length }}</Badge>
  </Button>

  <Dialog v-model:open="dialogOpen">
    <DialogContent class="grid-rows-[auto_minmax(0,1fr)_auto] max-h-[85dvh] gap-0 p-0 sm:max-w-[1180px]">
      <DialogHeader class="border-b px-6 py-4">
        <DialogTitle>Variant conditions</DialogTitle>
        <DialogDescription>
          Configure the values that select this variant. Missing user state defaults to an empty string, 0, or false based on its type; missing user metrics default to 0.
        </DialogDescription>
      </DialogHeader>

      <div class="min-h-0 overflow-y-auto">
        <div class="space-y-3 px-6 py-4">
          <RuleCard
            v-for="(rule, index) in draftRules"
            :key="rule.id"
            v-model="draftRules[index]"
            :index="index"
            :answerable-blocks="answerableBlocks"
            :all-blocks="allBlocks"
            :get-component-state-fields="getComponentStateFields"
            :errors="errorsForRule(index)"
            :show-action="false"
            @remove="draftRules.splice(index, 1)"
          />

          <div
            v-if="hasCurrentBlockCycle"
            role="alert"
            class="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12.5px] text-destructive"
          >
            These conditions create a component-state dependency cycle.
          </div>

          <Button type="button" variant="outline" @click="draftRules.push(createDraftRule())">
            <Icon name="lucide:plus" class="mr-2 size-4" />
            Add condition
          </Button>
        </div>
      </div>

      <DialogFooter class="border-t px-6 py-4">
        <Button type="button" variant="ghost" @click="dialogOpen = false">Cancel</Button>
        <Button type="button" :disabled="!canSave" @click="save">Save conditions</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
